import type { PaletteColor } from "@brexhq/metal-foundation";
import type { Theme } from "@emotion/react";
import React from "react";

import {
  type ButtonModifiers,
  ButtonSize,
} from "@brexhq/next/src/components/shared/Button/types";
import { ModuleThemeType } from "@brexhq/shared/types/ModuleTheme";
import {
  Icon,
  IconColors,
} from "@brexhq/shared/types/modules/BlockRowsModule/BlockRowItems/IconPicker";

export interface ButtonProps {
  label: string;
  onClick: (event: React.MouseEvent) => void;
  component: string;
  section?: string;
}
type MarginValue = null | void | string | number;
export type ButtonMargins = [
  MarginValue,
  MarginValue?,
  MarginValue?,
  MarginValue?,
];

export interface ButtonWithExternalLink {
  to?: string | null;
  label: string;
  toExternal?: string | null;
  noFollow?: boolean | null;
}

export interface InlineLinkProps
  extends React.AnchorHTMLAttributes<HTMLAnchorElement> {
  to: string;
  noFollow?: boolean | null;
  label: React.ReactNode;
  prefetch?: boolean;
  variant?: "legacy" | "default";
}

// export type LinkSize = "lrg" | "small";

export type ResponsiveButtonSizes = {
  LRG: ButtonSize;
  MED: ButtonSize;
  SML: ButtonSize;
};

export interface RebirthLinkProps
  extends React.AnchorHTMLAttributes<HTMLAnchorElement> {
  to: string;
  noFollow?: boolean | null;
  label: string;
  shallow?: boolean;
  scroll?: boolean;

  /**
   * Identifies the name of the component when emitting analytics events
   */
  component: string;

  /**
   * Identifies the sub-part of the component when emitting analytics events
   */
  section?: string;
  modifier?: LinkModifiers;
  children?: undefined; // we make sure it's undefined here.
  preventDefault?: boolean;
  size?: ResponsiveButtonSizes;
  margin?: ButtonMargins;
  theme?: ModuleThemeType;
  color?: PaletteColor;
  prefetch?: boolean;
  isFullWidth?: boolean;
  trackingId?: string;
  textStyle?: keyof Theme["typography"];
  mobileTextStyle?: keyof Theme["typography"];
  withTextAnimation?: boolean;
  variant?: ArrowLinkVariant;
  icon?: Icon;
  withIcon?: boolean;
  iconColor?: IconColors;
}

export interface RebirthLinkUnstyledProps
  extends React.AnchorHTMLAttributes<HTMLAnchorElement> {
  to: string;
  noFollow?: boolean | null;
  label: string;
  component: string;
  shallow?: boolean;
  scroll?: boolean;
  section?: string;
  modifier?: "unstyled"; // only have children for unstyled
  children: React.ReactNode;
  preventDefault?: boolean;
  margin?: ButtonMargins;
  theme?: ModuleThemeType;
  // TODO this type is inaccurate
  size?: ResponsiveButtonSizes;
  color?: PaletteColor;
  prefetch?: boolean;
  isFullWidth?: boolean;
  trackingId?: string;
  withTextAnimation?: boolean;
  variant?: ArrowLinkVariant;
  icon?: Icon;
  withIcon?: boolean;
  iconColor?: IconColors;
}

export type CombinedRebirthLinkProps =
  | RebirthLinkProps
  | RebirthLinkUnstyledProps;

export type ArrowLinkDirection = "left" | "right";
export type ArrowLinkVariant = "standard" | "bold";

export interface ArrowLinkProps
  extends React.AnchorHTMLAttributes<HTMLAnchorElement> {
  to: string;
  noFollow?: boolean | null;
  label: string;
  preventDefault?: boolean;
  size?: ResponsiveButtonSizes;
  margin?: ButtonMargins;
  color?: PaletteColor;
  prefetch?: boolean;
  direction?: ArrowLinkDirection;
  withTextAnimation?: boolean;
  variant?: ArrowLinkVariant;
  icon?: Icon;
  withIcon?: boolean;
  iconColor?: IconColors;
  theme?: ModuleThemeType;
}

export interface ButtonLinkProps
  extends React.AnchorHTMLAttributes<HTMLAnchorElement> {
  to: string;
  noFollow?: boolean | null;
  label: string;
  margin?: ButtonMargins;
  theme?: ModuleThemeType;
  size?: ResponsiveButtonSizes;
  modifier?: ButtonModifiers;
  prefetch?: boolean;
  isFullWidth?: boolean;
  icon?: Icon;
  withIcon?: boolean;
  iconColor?: IconColors;
}

export interface UnstyledLinkProps
  extends React.AnchorHTMLAttributes<HTMLAnchorElement> {
  to: string;
  noFollow?: boolean | null;
  label?: string;
  children?: React.ReactNode;
  margin?: ButtonMargins;
  prefetch?: boolean;
}

export const LinkModifierConsts = {
  backArrow: "backArrow",
  arrow: "arrow",
  primary: "primary",
  secondary: "secondary",
  legacyLink: "legacyLink",
  outline: "outline",
  inline: "inline",
  footer: "footer",
  nav: "nav",
  unstyled: "unstyled",
} as const;

export type LinkModifiers = keyof typeof LinkModifierConsts;
